"use client";

import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useRouter } from "next/navigation";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useCartStore } from "@/app/store/cartStore";
import Button from "./Button";

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const router = useRouter();
  const {
    items,
    removeItem,
    updateQuantity,
    clearCart,
    getTotalPrice,
    getTotalItems,
  } = useCartStore();

  const handleCheckout = () => {
    onClose();
    router.push("/Checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      {/* Updated z-index to be higher than navbar (z-[100]) */}
      <SheetContent className="w-full sm:w-[450px] backdrop-blur-md backdrop-filter supports-[backdrop-filter]:bg-card/95 z-[10001]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="text-xl font-bold text-heading">Your Cart</div>
              <div className="text-sm text-text">
                {getTotalItems()} {getTotalItems() === 1 ? "item" : "items"}
              </div>
            </div>
          </SheetTitle>
          <SheetDescription className="sr-only">
            Shopping cart with selected menu items
          </SheetDescription>
        </SheetHeader>

        {/* Cart Items */}
        <div className="my-5 h-[calc(100vh-13rem)] overflow-auto rounded-xl border border-border">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12 px-4">
              <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mb-4">
                <ShoppingBag className="w-12 h-12 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-heading mb-2">
                Your cart is empty
              </h3>
              <p className="text-text text-sm mb-6">
                Add some delicious items to get started!
              </p>
              <Button variant="primary" onClick={onClose}>
                Browse Menu
              </Button>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {items.map((item) => (
                <div
                  key={item.item_id}
                  className="rounded-lg p-[1px] bg-gradient-to-r from-primary/20 to-transparent"
                >
                  <div className="flex gap-4 rounded-lg bg-card p-3 hover:bg-muted/50 transition-colors">
                    {/* Item Image */}
                    {/* <div className="w-20 h-20 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <ShoppingBag className="w-8 h-8" />
                        </div>
                      )}
                    </div> */}
                    <ShoppingBag className="w-8 h-8" />

                    {/* Item Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-heading truncate text-sm">
                            {item.name}
                          </h3>
                          <p className="text-xs text-text/70 truncate">
                            {item.name_ar}
                          </p>
                        </div>
                        <button
                          onClick={() => removeItem(item.item_id)}
                          className="ml-2 text-red-500 hover:text-red-600 transition-colors flex-shrink-0 p-1"
                          aria-label="Remove item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Quantity Controls & Price */}
                      <div className="flex items-center justify-between">
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              updateQuantity(item.item_id, item.quantity - 1)
                            }
                            className="w-7 h-7 rounded-lg bg-muted border border-border hover:border-primary hover:bg-primary/5 flex items-center justify-center text-heading transition-all"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="w-3 h-3" />
                          </button>

                          <span className="w-8 text-center font-semibold text-heading text-sm">
                            {item.quantity}
                          </span>

                          <button
                            onClick={() =>
                              updateQuantity(item.item_id, item.quantity + 1)
                            }
                            className="w-7 h-7 rounded-lg bg-muted border border-border hover:border-primary hover:bg-primary/5 flex items-center justify-center text-heading transition-all"
                            aria-label="Increase quantity"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          <div className="font-bold text-primary text-sm">
                            {(item.price * item.quantity).toFixed(2)} SR
                          </div>
                          <div className="text-xs text-text/60">
                            {item.price} SR each
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Clear Cart Button */}
              {items.length > 0 && (
                <button
                  onClick={clearCart}
                  className="w-full py-2 text-sm text-red-500 hover:text-red-600 transition-colors font-medium"
                >
                  Clear Cart
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer with Total & Checkout */}
        {items.length > 0 && (
          <SheetFooter className="flex-col gap-4">
            {/* Total */}
            <div className="flex items-center justify-between w-full px-1">
              <div>
                <p className="text-sm text-text">Total</p>
                <p className="text-xs text-text/60">{getTotalItems()} items</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-primary">
                  {getTotalPrice().toFixed(2)} SR
                </div>
              </div>
            </div>

            {/* Checkout Button */}
            <div className="flex w-full gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Continue Shopping
              </Button>
              <Button
                onClick={handleCheckout}
                className="flex-1 bg-gradient-to-r from-primary to-primary/80"
              >
                <span>Checkout</span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
